import React from 'react';
import Styled from './styled';
import {
    linear,
    useGlowConfigArray,
    GlowConfig,
    quadratic,
} from '../../utils/animationUtils';

// input is 0 to 1 (time)
// output is position between 0 and 1
const glowPosition = (time: number): number => {
    if (time < 0.2) {
        return quadratic(37.5, -15.25, 1.5, time);
    }
    if (time < 0.3) {
        return -0.05;
    }
    return linear([0.3, -0.05], [0.85, 1.0], time);
};

export interface ReasoningIndicatorProps {
    /** Accessibility support (aria-label, defaults to match text) */
    ariaLabel?: string;
    /** The text to animate */
    text: string;
}

/**
 * ReasoningIndicator component displays an animated text indicator
 * with letter-by-letter wave animation effect.
 * 
 * Features:
 * - Letter-by-letter wave animation
 * - Accessibility support with aria-label and role
 * - Respects user's reduced motion preferences
 * - Customizable text and participant role styling
 * 
 * @param {ReasoningIndicatorProps} props - The properties to configure the ReasoningIndicator component.
 * @returns {JSX.Element} The rendered ReasoningIndicator component.
 */
export const ReasoningIndicator: React.FC<ReasoningIndicatorProps> = ({
    text,
    ariaLabel = text,
}) => {
    // Split text into individual letters for animation
    const letters = text.split('');
    const glowConfigArray = useGlowConfigArray(letters.length, glowPosition);

    return (
        <Styled.AnimatedContainer
            role="status"
            aria-live="polite"
            aria-label={ariaLabel}
        >
            <Styled.ReasoningTextContainer>
                {letters.map((letter, index) => (
                    <Styled.AnimatedLetterConfigurable
                        key={`${letter}-${index}`}
                        glowConfig={glowConfigArray[index]}
                    >
                        {/* Convert spaces to non-breaking spaces to prevent HTML space collapse */}
                        {letter === ' ' ? '\u00A0' : letter}
                    </Styled.AnimatedLetterConfigurable>
                ))}
            </Styled.ReasoningTextContainer>
        </Styled.AnimatedContainer>
    );
};
